// Path: libs/core/theme/src/lib/data-access/brand-kernel.service.ts
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { APP_CONFIG, AppConfig } from '@cardapio-online/config';
import { firstValueFrom } from 'rxjs';

type BrandPatch = Partial<
  Pick<AppConfig, 'appName' | 'brandPrimary' | 'brandLogoUrl' | 'mascoteLogo'>
>;

@Injectable({
  providedIn: 'root',
})
export class BrandKernelService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly fallbackConfig = inject(APP_CONFIG);

  private readonly state = signal<AppConfig>({
    ...this.fallbackConfig,
  });

  readonly config = computed(() => this.state());

  readonly appName = computed(() => this.state().appName);
  readonly brandPrimary = computed(() => this.state().brandPrimary);
  readonly brandLogoUrl = computed(() => this.state().brandLogoUrl);
  readonly mascoteLogo = computed(() => this.state().mascoteLogo);

  private eventSource?: EventSource;

  private get apiUrl(): string {
    return this.fallbackConfig.brandKernelApiUrl ?? 'http://localhost:3333/brand-config';
  }

  private get cacheKey(): string {
    return `brand.config.${this.fallbackConfig.tenantSlug}`;
  }

  constructor() {
    effect(() => {
      const config = this.state();

      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      this.applyCssVariables(config);
      this.document.title = config.appName || this.fallbackConfig.appName;
    });
  }

  async bootstrap(): Promise<void> {
    this.restoreFromCache();

    try {
      await this.refresh();
    } catch {
      /**
       * POC:
       * Se o mock na porta 3333 estiver desligado, o app continua usando APP_CONFIG.
       */
    }

    this.connectEvents();
  }

  async refresh(): Promise<void> {
    const config = await firstValueFrom(this.http.get<AppConfig>(this.apiUrl));
    this.apply(config);
  }

  async update(patch: BrandPatch): Promise<void> {
    const config = await firstValueFrom(this.http.put<AppConfig>(this.apiUrl, patch));
    this.apply(config);
  }

  private connectEvents(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.fallbackConfig.brandRealtimeEnabled) {
      return;
    }

    if (this.eventSource) {
      return;
    }

    this.eventSource = new EventSource(`${this.apiUrl}/events`);

    this.eventSource.addEventListener('brand-config', (event) => {
      const message = event as MessageEvent<string>;

      try {
        const nextConfig = JSON.parse(message.data) as AppConfig;

        this.zone.run(() => {
          this.apply(nextConfig);
        });
      } catch {
        /**
         * POC:
         * Evento inválido não deve quebrar a experiência.
         */
      }
    });

    this.eventSource.onerror = () => {
      /**
       * POC:
       * EventSource reconecta automaticamente.
       * Mantemos silencioso para não poluir a UI.
       */
    };
  }

  private apply(config: AppConfig): void {
    const next = {
      ...this.fallbackConfig,
      ...config,
    };

    this.state.set(next);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.cacheKey, JSON.stringify(next));
    }
  }

  private restoreFromCache(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const raw = localStorage.getItem(this.cacheKey);

    if (!raw) {
      return;
    }

    try {
      this.state.set({
        ...this.fallbackConfig,
        ...JSON.parse(raw),
      });
    } catch {
      localStorage.removeItem(this.cacheKey);
    }
  }

  private applyCssVariables(config: AppConfig): void {
    const root = this.document.documentElement;
    const primary = config.brandPrimary || this.fallbackConfig.brandPrimary;
    const brandLogoUrl = config.brandLogoUrl || this.fallbackConfig.brandLogoUrl;
    const mascoteLogo = config.mascoteLogo || this.fallbackConfig.mascoteLogo;

    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--shell-brand', primary);
    root.style.setProperty('--accent', primary);
    root.style.setProperty('--ds-color-primary', primary);

    root.style.setProperty('--brand-logo-url', `url("${brandLogoUrl}")`);
    root.style.setProperty('--mascote-logo-url', `url("${mascoteLogo}")`);

    root.dataset['brandVersion'] = config.version ?? 'static';
  }
}